import React, { useCallback, useEffect, useState } from 'react';
import { api } from '@/lib/api';
import { useI18n } from '@/lib/i18n';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Plus, Trash2, Truck } from 'lucide-react';
import { toast } from 'sonner';

export default function Suppliers() {
  const { t, lang } = useI18n();
  const [suppliers, setSuppliers] = useState([]);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const load = useCallback(async () => { const r = await api.get('/suppliers'); setSuppliers(r.data); }, []);
  useEffect(() => { load(); }, [load]);
  return (
    <div className="space-y-4" data-testid="suppliers-page">
      <div className="flex items-center justify-between">
        <h1 className="text-2xl font-heading font-bold">{t('nav_suppliers')}</h1>
        <Button onClick={() => { setEditing(null); setOpen(true); }} data-testid="suppliers-new-button"><Plus className="h-4 w-4 mr-1" /> {t('new_supplier')}</Button>
      </div>
      {suppliers.length === 0 ? (
        <Card><CardContent className="text-center py-12">
          <Truck className="h-10 w-10 text-[hsl(var(--muted-foreground))] mx-auto mb-3" />
          <p className="text-sm text-[hsl(var(--muted-foreground))]">{lang === 'es' ? 'Sin proveedores' : 'Sem fornecedores'}</p>
        </CardContent></Card>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
          {suppliers.map(s => (
            <Card key={s.id} className="cursor-pointer hover:shadow-md transition-shadow" onClick={() => { setEditing(s); setOpen(true); }} data-testid={`supplier-card-${s.id}`}>
              <CardContent className="p-4">
                <h3 className="font-heading font-semibold">{s.nombre}</h3>
                {s.nif && <div className="text-xs font-mono text-[hsl(var(--muted-foreground))]">{s.nif}</div>}
                <div className="text-xs text-[hsl(var(--muted-foreground))] mt-2 space-y-0.5">
                  {s.email && <div>{s.email}</div>}
                  {s.telefono && <div>{s.telefono}</div>}
                  {s.direccion && <div>{s.direccion}</div>}
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}
      <SupplierDialog open={open} onOpenChange={setOpen} editing={editing} t={t} onSaved={load} />
    </div>
  );
}

function SupplierDialog({ open, onOpenChange, editing, t, onSaved }) {
  const [form, setForm] = useState({ nombre: '', nif: '', email: '', telefono: '', direccion: '', notas: '' });
  useEffect(() => {
    if (editing) setForm(editing);
    else setForm({ nombre: '', nif: '', email: '', telefono: '', direccion: '', notas: '' });
  }, [editing, open]);
  const save = async () => {
    if (editing) await api.put(`/suppliers/${editing.id}`, form);
    else await api.post('/suppliers', form);
    toast.success('OK'); onOpenChange(false); onSaved();
  };
  const remove = async () => { await api.delete(`/suppliers/${editing.id}`); onOpenChange(false); onSaved(); };
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent data-testid="supplier-dialog">
        <DialogHeader><DialogTitle>{editing ? t('edit') : t('new_supplier')}</DialogTitle></DialogHeader>
        <div className="space-y-3">
          <div><Label>{t('name')}</Label><Input value={form.nombre} onChange={e => setForm({ ...form, nombre: e.target.value })} data-testid="supplier-name-input" /></div>
          <div className="grid grid-cols-2 gap-3">
            <div><Label>{t('nif')}</Label><Input value={form.nif} onChange={e => setForm({ ...form, nif: e.target.value })} /></div>
            <div><Label>{t('phone')}</Label><Input value={form.telefono} onChange={e => setForm({ ...form, telefono: e.target.value })} /></div>
          </div>
          <div><Label>{t('email')}</Label><Input value={form.email} onChange={e => setForm({ ...form, email: e.target.value })} /></div>
          <div><Label>{t('address')}</Label><Input value={form.direccion} onChange={e => setForm({ ...form, direccion: e.target.value })} /></div>
        </div>
        <DialogFooter className="gap-2">
          {editing && <Button variant="destructive" onClick={remove} data-testid="supplier-delete-button"><Trash2 className="h-4 w-4" /></Button>}
          <Button onClick={save} disabled={!form.nombre} data-testid="supplier-save-button">{t('save')}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
